import { Card, Row, Col, Typography, Space, Progress, Statistic, Button, Tag } from 'antd'
import { 
  RocketOutlined, 
  ExperimentOutlined, 
  BulbOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  CloudServerOutlined,
  BarChartOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'

const { Title, Text, Paragraph } = Typography

const FallbackAppDashboard = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: <ExperimentOutlined style={{ fontSize: '32px', color: '#8b5cf6' }} />,
      title: 'Experimental Features',
      description: 'Test cutting-edge capabilities',
      status: 'active',
      progress: 75
    },
    {
      icon: <ApiOutlined style={{ fontSize: '32px', color: '#06b6d4' }} />,
      title: 'API Playground',
      description: 'Interactive API testing environment',
      status: 'beta',
      progress: 60
    },
    {
      icon: <CloudServerOutlined style={{ fontSize: '32px', color: '#10b981' }} />,
      title: 'Infrastructure Monitor',
      description: 'Real-time system monitoring',
      status: 'stable',
      progress: 90
    },
    {
      icon: <BulbOutlined style={{ fontSize: '32px', color: '#f59e0b' }} />,
      title: 'Innovation Lab',
      description: 'Prototype new ideas',
      status: 'alpha',
      progress: 30
    }
  ]

  const metrics = [
    { title: 'Active Experiments', value: 12, suffix: '', color: '#8b5cf6' },
    { title: 'API Calls Today', value: 3.4, suffix: 'K', color: '#06b6d4' },
    { title: 'System Uptime', value: 99.9, suffix: '%', color: '#10b981' },
    { title: 'Lab Projects', value: 7, suffix: '', color: '#f59e0b' }
  ]

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'stable': return 'green'
      case 'active': return 'blue'
      case 'beta': return 'purple'
      case 'alpha': return 'orange'
      default: return 'default'
    }
  }

  return (
    <div style={{ 
      width: '100vw', 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 0,
      margin: 0,
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RocketOutlined /> Fallback App
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Experimental Features & Testing Ground
            </Text>
          </div>
        </div>
        <Space>
          <Button 
            type="primary" 
            icon={<SettingOutlined />}
            onClick={() => navigate('/fallbackapp/settings')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            Settings
          </Button>
        </Space>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>
        {/* Metrics Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {metrics.map((metric, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Statistic
                  title={<Text style={{ color: '#6b7280' }}>{metric.title}</Text>}
                  value={metric.value}
                  suffix={metric.suffix}
                  valueStyle={{ color: metric.color, fontSize: '28px' }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Features Grid */}
        <Title level={4} style={{ color: '#ffffff', marginBottom: '16px' }}>
          Available Modules
        </Title>
        <Row gutter={[16, 16]}>
          {features.map((feature, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card
                hoverable
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  height: '100%',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease',
                }}
                bodyStyle={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: '24px'
                }}
                onClick={() => navigate(`/fallbackapp/${feature.title.toLowerCase().replace(/\s+/g, '-')}`)}
              >
                {feature.icon}
                <Title level={5} style={{ marginTop: '16px', marginBottom: '8px' }}>
                  {feature.title}
                </Title>
                <Tag color={getStatusColor(feature.status)} style={{ marginBottom: '12px' }}>
                  {feature.status.toUpperCase()}
                </Tag>
                <Paragraph style={{ color: '#6b7280', marginBottom: '16px', flex: 1 }}>
                  {feature.description}
                </Paragraph>
                <Progress 
                  percent={feature.progress} 
                  size="small" 
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                  style={{ width: '100%' }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Activity Section */}
        <Card
          style={{
            marginTop: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0 }}>
              <ThunderboltOutlined /> Recent Activity
            </Title>
            <Button type="link" icon={<BarChartOutlined />}>
              View All
            </Button>
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            {[
              { time: '2 hours ago', event: 'New experiment deployed: Neural Network v2.3', type: 'experiment' },
              { time: '5 hours ago', event: 'API endpoint tested: /api/v3/predict', type: 'api' },
              { time: '1 day ago', event: 'Infrastructure scaled to 8 nodes', type: 'infrastructure' },
              { time: '2 days ago', event: 'Innovation project initiated: Quantum Computing Interface', type: 'innovation' }
            ].map((activity, index) => (
              <div 
                key={index}
                style={{
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  borderLeft: '3px solid',
                  borderLeftColor: activity.type === 'experiment' ? '#8b5cf6' :
                                  activity.type === 'api' ? '#06b6d4' :
                                  activity.type === 'infrastructure' ? '#10b981' : '#f59e0b'
                }}
              >
                <Text type="secondary" style={{ fontSize: '12px' }}>{activity.time}</Text>
                <div>{activity.event}</div>
              </div>
            ))}
          </Space>
        </Card>
      </div>
    </div>
  )
}

export default FallbackAppDashboard